import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import { Observable, of } from 'rxjs';
import { map, switchMap, filter, shareReplay, first } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { DeleteDialogComponent, DeleteDialogResult } from '../../dialogs/delete-dialog/delete-dialog.component';
import { FwLegacyDialogService as DialogService } from '../../dialogs/dialog.service';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { downloadFile } from '../../shared/browser.utils';
import { getComputedData } from '../../shared/caching.utils';
import { getContainerType, pluralize, getContainerProjectId, getRoleMatrix } from '../../shared/container.utils';
import { ContainerAccess } from '../../shared/enums/container-access.enum';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { JobState } from '../../shared/enums/job-state.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Analysis } from '../../shared/models/analysis.model';
import { Container, ContainerReference } from '../../shared/models/container.model';
import { File } from '../../shared/models/file.model';
import { Gear } from '../../shared/models/gear.model';
import { Job } from '../../shared/models/job.model';
import { ViewerApp } from '../../shared/models/site-settings.model';
import { ViewerService } from '../../viewers/viewer.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { FwIconModule } from '@flywheel-io/vision';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';
import { JobLogComponent } from '../job-log/job-log.component';

@Component({
  selector: 'analysis-results',
  templateUrl: './analysis-results.component.html',
  styleUrls: ['./analysis-results.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    FwIconModule,
    MaterialModule,
    MatTabsModule,
    PipesModule,
    ReactiveFormsModule,
    SharedModule,
    JobLogComponent,
  ],
})
export class AnalysisResultsComponent implements OnInit, OnChanges {
  @Input() analysis: Analysis
  @Input() container: Container
  @Input() expanded = false

  @Output() afterDelete = new EventEmitter<Analysis>()

  canDeleteAnalysis = false
  canDownloadFiles = false
  canEditAnalysis = false
  config$: Observable<ConfigItem[]>
  editLabel: string = null
  gear$: Observable<Gear>
  job$: Observable<Job>
  jobState$: Observable<JobState>
  jobPrioritization = this.config.isFeatureEnabled('job_prioritization')
  viewerApps$: Observable<ViewerApp[]>

  private fileParents = new WeakMap<File, ContainerReference>()

  constructor(
    private config: ConfigService,
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    private viewerService: ViewerService,
  ) { }

  ngOnInit(): void {
    this.job$ = typeof this.analysis.job === 'string'
      ? this.coreApi.jobs.get(this.analysis.job).pipe(shareReplay(1))
      : of(this.analysis.job);
    this.jobState$ = this.job$.pipe(
      map(job => job && job.state || null),
    );
    this.config$ = this.job$.pipe(
      map(job => {
        if (!job) {
          return null;
        }
        if (!(job.config && job.config.config)) {
          return [];
        }
        const config = Object.entries(job.config.config).map(([key, value]) => ({ key, value: value as ConfigValue }));
        return sortBy(config, 'key');
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.analysis && !this.analysis.container_type) {
      // analyses don't have any other identifying properties
      this.analysis.container_type = ContainerType.Analysis;
    }

    if (changes.container && this.container) {
      const projectId = getContainerProjectId(this.container);
      this.viewerApps$ = this.viewerService.getAssociatedApps({ container: this.analysis, projectId });
      this.updatePermissions();
    }
  }

  updatePermissions(): void {
    const projectId = getContainerProjectId(this.container);
    if (projectId) {
      this.projectsService.getProjectPermissions(projectId).subscribe(projectPermissions => {
        this.canEditAnalysis = projectPermissions.includes(PermissionAction.AnalysesModifyMetadata);
        this.canDeleteAnalysis = projectPermissions.includes(PermissionAction.AnalysesDelete);
        this.canDownloadFiles = projectPermissions.includes(PermissionAction.FilesDownload);
      });
    } else {
      // Fall back to roleMatrix if the container doesn't have roles
      this.profileService.getProfile().pipe(first()).subscribe(profile => {
        const roleMatrix = getRoleMatrix(this.container, profile);
        this.canEditAnalysis = roleMatrix[ContainerAccess.ReadWrite];
        this.canDeleteAnalysis = roleMatrix[ContainerAccess.ReadWrite];
        this.canDownloadFiles = roleMatrix[ContainerAccess.ReadOnly];
      });
    }
  }

  getFileParent(file: File): ContainerReference {
    return getComputedData(this.fileParents, file, () => {
      const containerType = file.parent_ref?.type || ContainerType.Analysis;
      return {
        _id: file.parent_ref?.id || this.analysis._id,
        container_type: containerType,
        parents: file.parents ?? omit(this.analysis.parents, containerType),
        analysis: this.analysis,
      };
    });
  }

  toggleDataPanel(): void {
    this.expanded = !this.expanded;
    if (this.expanded) {
      if (this.analysis.gear_info) {
        this.gear$ = this.coreApi.gears.get(this.analysis.gear_info.id);
      }
      // core-api omits `info` from list entries
      // old core sends empty `info` and an `info_exists` boolean
      if (!this.analysis.info || (this.analysis.info_exists && isEmpty(this.analysis.info))) {
        this.coreApi.analyses.get(this.analysis._id).subscribe(({ info }) => {
          this.analysis.info = info;
        });
      }
    }
  }

  saveAnalysisLabel(): void {
    if (!this.editLabel) {
      return;
    }
    this.coreApi.analyses.put(this.analysis._id, { label: this.editLabel }).subscribe({
      next: () => {
        this.analysis.label = this.editLabel;
        this.editLabel = null;
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('analysis.update.error');
      },
    });
  }

  deleteAnalysis(): void {
    this.dialog.open(DeleteDialogComponent, { data: { content: 'Are you sure you want to delete this analysis?' } }).afterClosed().pipe(
      filter((result: DeleteDialogResult) => Boolean(result.delete)),
      switchMap(result => {
        const containerType = getContainerType(this.container);
        return this.coreApi.analyses.delete(pluralize(containerType), this.container._id, this.analysis._id, result.deleteReason ? { delete_reason: result.deleteReason } : {});
      }),
    ).subscribe({
      next: () => {
        this.notifications.success('analysis.delete.success');
        this.afterDelete.emit(this.analysis);
      },
      error: error => {
        this.logger.error(error);
        let errorPath = 'analysis.delete.error';
        if (error.error?.message?.startsWith('analysis_conflict:')) {
          errorPath = 'analysis.delete.analysisConflictError';
        }
        this.notifications.error(errorPath);
      },
    });
  }

  downloadAnalysisFiles(): void {
    this.coreApi.downloads.createTicket({
      optional: true,
      nodes: [{ level: 'analysis', _id: this.analysis._id }],
    }).subscribe({
      next: ({ filename, ticket }) => {
        downloadFile(filename, this.config.getApiUrl(`/api/download?ticket=${ticket}&format=zip`));
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('download.files.error');
      },
    });
  }

  viewInApp(app?: ViewerApp): void {
    this.viewerService.view({
      app: app,
      container: this.analysis,
    }).subscribe();
  }
}

type ConfigValue = string | number | boolean;
interface ConfigItem {
  key: string
  value: ConfigValue
}
